import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { api } from '../../services/api';
import { useAuth } from '../../components/auth/AuthContext';
import * as Sentry from "@sentry/react";
import { 
  Loader2, 
  AlertTriangle, 
  Info,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Calendar
} from 'lucide-react';

// Import shared utilities (from BillingUtils.js)
import { 
  useResponsive,
  formatDate,
  safeParseFloat,
  currencyUtils,
  logError,
  logInfo,
  logUserAction,
  logPerformance,
  StatusBadge,
  Pagination,
  getNetworkErrorMessage 

} from './BillingUtils';
import { useNotification } from '../../Notifications';

// Date validation constants
const SERVICE_START_DATE = '2025-01-01';

// Helper function to get current date in YYYY-MM-DD format
const getCurrentDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Helper function to validate date
const validateDate = (dateString) => {
  if (!dateString) return { valid: true };
  
  const date = new Date(dateString + 'T00:00:00');
  const minDate = new Date(SERVICE_START_DATE + 'T00:00:00');
  const maxDate = new Date(getCurrentDate() + 'T00:00:00');
  
  if (isNaN(date.getTime())) {
    return { valid: false, error: 'Invalid date format' };
  }
  
  if (date < minDate) {
    return { valid: false, error: `Date cannot be before ${SERVICE_START_DATE}` };
  }
  
  if (date > maxDate) {
    return { valid: false, error: 'Date cannot be in the future' };
  }
  
  return { valid: true };
};

// Helper function to validate date range
const validateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return { valid: true };
  
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  
  if (start > end) {
    return { valid: false, error: 'Start date must be less than or equal to end date' };
  }
  
  return { valid: true };
};

// Error Messages for Rate Limiting
const ERROR_MESSAGES = {
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'AUTH_ERROR': 'Please log in again to continue',
  'ACCESS_DENIED': 'You do not have permission to perform this action',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later',
  'NETWORK_ERROR': 'Network connection error. Please check your connection and try again',
  'SERVER_ERROR': 'An internal error occurred. Please try again later',
  'VALIDATION_ERROR': 'Invalid request data',
  'MISSING_PARAMETER': 'Required parameter is missing',
  'RESOURCE_NOT_FOUND': 'Deposit not found',
  'RESOURCE_ALREADY_EXISTS': 'A deposit with this information already exists',
  'CURRENCY_NOT_SET': 'Please set your preferred currency in account settings',
  'INVALID_PAGINATION': 'Invalid pagination parameters',
  // Network Errors (add at end of ERROR_MESSAGES object)
'OFFLINE': "You're offline. Check your connection.",
'TIMEOUT': "Request timed out. Please try again.",
'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
'NO_RESPONSE': "No response from server. Please try again.",
'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again."
};

// Safe error message function with backend error structure support
// Safe error message function with backend error structure support
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  // Primary: Use normalized error message from interceptor
  if (error.error) {
    return error.error;
  }
  
  // Fallback: Check error code against known messages
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  return 'Something went wrong. Please try again.';
};

// Extract request ID from response for correlation with backend logging
const extractRequestId = (response) => {
  if (!response) return null;
  
  // Check response data for request_id (backend includes this)
  if (response.request_id) return response.request_id;
  
  // Check nested data object
  if (response.data && response.data.request_id) return response.data.request_id;
  
  // Check headers for request ID
  if (response.headers && response.headers['x-request-id']) {
    return response.headers['x-request-id'];
  }
  
  return null;
};

// Process security headers from backend response
const processSecurityHeaders = (response) => {
  if (!response || !response.headers) return;
  
  const securityHeaders = {};
  
  // Extract security headers that backend adds via add_security_headers
  if (response.headers['x-content-type-options']) {
    securityHeaders['x-content-type-options'] = response.headers['x-content-type-options'];
  }
  
  if (response.headers['x-frame-options']) {
    securityHeaders['x-frame-options'] = response.headers['x-frame-options'];
  }
  
  if (response.headers['x-xss-protection']) {
    securityHeaders['x-xss-protection'] = response.headers['x-xss-protection'];
  }
  
  if (Object.keys(securityHeaders).length > 0) {
    logInfo('Security headers processed', { securityHeaders });
  }
};

// Enhanced logging with backend correlation
const logBackendCorrelation = (action, data = {}) => {
  const correlationData = {
    ...data,
    frontend_timestamp: Date.now(),
    action_type: action
  };
  
  if (data.request_id) {
    correlationData.backend_request_id = data.request_id;
  }
  
  logUserAction(`frontend_${action}`, correlationData);
};

// Performance: Debounce function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

const DepositsSmartFilterContainer = ({ filters, setFilters, applyFilters, resetFilters, currency, isMobile, isTablet }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const dropdownRef = useRef(null);
  const filterBoxRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dateValidationError, setDateValidationError] = useState('');
  
  // Track temporary filter state before applying
  const [tempFilters, setTempFilters] = useState({
    startDate: filters.startDate,
    endDate: filters.endDate,
    minAmount: filters.minAmount,
    maxAmount: filters.maxAmount
  });
  
  // Custom amount range mode
  const [customAmountMode, setCustomAmountMode] = useState(false);
  
  useEffect(() => {
    logBackendCorrelation("deposits_smart_filter_external_filters_changed", { filters });
    // Update tempFilters when filters change externally
    setTempFilters({
      startDate: filters.startDate,
      endDate: filters.endDate,
      minAmount: filters.minAmount,
      maxAmount: filters.maxAmount
    });
  }, [filters]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        logBackendCorrelation('deposits_filter_dropdown_closed', {
          wasOpen: isOpen,
          reason: 'click_outside'
        });
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update dropdown position when it opens
  useEffect(() => {
    if (isOpen && filterBoxRef.current) {
      const rect = filterBoxRef.current.getBoundingClientRect();
      const newPosition = {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      };
      logBackendCorrelation("deposits_smart_filter_dropdown_position_updated", newPosition);
      setDropdownPosition(newPosition);
    }
  }, [isOpen]);
  
  const toggleDropdown = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    
    logBackendCorrelation('deposits_filter_dropdown_toggled', {
      isOpen: newIsOpen,
      currentFilters: filters,
      isMobile,
      isTablet
    });
  };
  
  const toggleSection = (section) => {
    const newSection = activeSection === section ? null : section;
    setActiveSection(newSection);
    
    logBackendCorrelation('deposits_filter_section_toggled', {
      section,
      wasActive: activeSection === section,
      newSection
    });
  };
  
  const handleFilterChange = (type, value) => {
    // Input sanitization for amount fields
    let sanitizedValue = value;
    if (type === 'minAmount' || type === 'maxAmount') {
      sanitizedValue = value.replace(/[^\d.]/g, '');
      // Prevent multiple decimal points
      const parts = sanitizedValue.split('.');
      if (parts.length > 2) {
        sanitizedValue = parts[0] + '.' + parts.slice(1).join('');
      }
    }
    
    // Validate dates immediately when they change
    if (type === 'startDate' || type === 'endDate') {
      const newStartDate = type === 'startDate' ? value : tempFilters.startDate;
      const newEndDate = type === 'endDate' ? value : tempFilters.endDate;
      
      // Clear previous errors
      setDateValidationError('');
      
      // Validate the changed date
      const dateValidation = validateDate(value);
      if (!dateValidation.valid) {
        setDateValidationError(dateValidation.error);
        logBackendCorrelation('deposits_filter_date_validation_error', {
          filterType: type,
          value,
          error: dateValidation.error
        });
        return;
      }
      
      // Validate date range if both dates are set
      if (newStartDate && newEndDate) {
        const rangeValidation = validateDateRange(newStartDate, newEndDate);
        if (!rangeValidation.valid) {
          setDateValidationError(rangeValidation.error);
          logBackendCorrelation('deposits_filter_date_range_validation_error', {
            startDate: newStartDate,
            endDate: newEndDate,
            error: rangeValidation.error
          });
          return;
        }
      }
    }
    
    logBackendCorrelation('deposits_filter_temp_changed', {
      filterType: type,
      originalValue: value,
      sanitizedValue,
      previousValue: tempFilters[type]
    });
    
    setTempFilters(prev => ({
      ...prev,
      [type]: sanitizedValue
    }));
  };
  
  const handleApplyFilters = () => {
    // Validate dates before applying
    const startDateValidation = validateDate(tempFilters.startDate);
    if (!startDateValidation.valid) {
      setDateValidationError(startDateValidation.error);
      logBackendCorrelation('deposits_filter_apply_validation_error', {
        field: 'startDate',
        error: startDateValidation.error
      });
      return;
    }
    
    const endDateValidation = validateDate(tempFilters.endDate);
    if (!endDateValidation.valid) {
      setDateValidationError(endDateValidation.error);
      logBackendCorrelation('deposits_filter_apply_validation_error', {
        field: 'endDate',
        error: endDateValidation.error
      });
      return;
    }
    
    const rangeValidation = validateDateRange(tempFilters.startDate, tempFilters.endDate);
    if (!rangeValidation.valid) {
      setDateValidationError(rangeValidation.error);
      logBackendCorrelation('deposits_filter_apply_validation_error', {
        field: 'dateRange',
        error: rangeValidation.error
      });
      return;
    }
    
    // Clear validation errors
    setDateValidationError('');
    
    logBackendCorrelation('deposits_filters_applied', {
      appliedFilters: tempFilters,
      previousFilters: filters
    });
    
    // Apply the tempFilters
    setFilters(tempFilters);
    applyFilters(tempFilters, false); // false indicates this is not a page change
    setIsOpen(false);
  };
  
  const handleResetFilters = () => {
    logBackendCorrelation('deposits_filters_reset', {
      previousFilters: filters
    });
    
    resetFilters();
    const emptyFilters = {
      startDate: '',
      endDate: '',
      minAmount: '',
      maxAmount: ''
    };
    setTempFilters(emptyFilters);
    setDateValidationError('');
    setIsOpen(false);
  };
  
  // Define currency-specific presets with validation
  const getAmountPresets = () => {
    try {
      switch(currency) {
        case 'INR':
          return [
            { label: 'Under ₹1,000', min: null, max: 1000 },
            { label: '₹1,000 - ₹5,000', min: 1000, max: 5000 },
            { label: '₹5,000 - ₹10,000', min: 5000, max: 10000 },
            { label: 'Over ₹10,000', min: 10000, max: null }
          ];
        case 'EUR':
          return [
            { label: 'Under €100', min: null, max: 100 },
            { label: '€100 - €500', min: 100, max: 500 },
            { label: '€500 - €1,000', min: 500, max: 1000 },
            { label: 'Over €1,000', min: 1000, max: null }
          ];
        case 'USD':
        default:
          return [
            { label: 'Under $100', min: null, max: 100 },
            { label: '$100 - $500', min: 100, max: 500 },
            { label: '$500 - $1,000', min: 500, max: 1000 },
            { label: 'Over $1,000', min: 1000, max: null }
          ];
      }
    } catch (error) {
      logError("deposits_smart_filter_get_amount_presets_error", { currency }, error);
      return [];
    }
  };
  
  const handlePresetSelect = (min, max) => {
    logBackendCorrelation('deposits_filter_preset_selected', {
      preset: { min, max },
      currency
    });
    
    setTempFilters(prev => ({
      ...prev,
      minAmount: min ? min.toString() : '',
      maxAmount: max ? max.toString() : ''
    }));
  };
  
  // Helper to get display text for active filters with error handling
  const getActiveFiltersText = () => {
    try {
      const activeFilters = [];
      
      if (filters.startDate && filters.endDate) {
        activeFilters.push(`Date: ${filters.startDate} to ${filters.endDate}`);
      } else if (filters.startDate) {
        activeFilters.push(`From: ${filters.startDate}`);
      } else if (filters.endDate) {
        activeFilters.push(`Until: ${filters.endDate}`);
      }
      
      if (filters.minAmount && filters.maxAmount) {
        activeFilters.push(`Amount: ${currencyUtils.getSymbol(currency)}${filters.minAmount} - ${currencyUtils.getSymbol(currency)}${filters.maxAmount}`);
      } else if (filters.minAmount) {
        activeFilters.push(`Min: ${currencyUtils.getSymbol(currency)}${filters.minAmount}`);
      } else if (filters.maxAmount) {
        activeFilters.push(`Max: ${currencyUtils.getSymbol(currency)}${filters.maxAmount}`);
      }
      
      return activeFilters.length > 0 
        ? activeFilters.join(', ') 
        : 'Filter Credits';
    } catch (error) {
      logError("deposits_smart_filter_get_active_filters_text_error", { filters, currency }, error);
      return 'Filter Deposits';
    }
  };

  return (
    <div ref={dropdownRef} style={{
      backgroundColor: '#1a1a1a',
      borderRadius: '10px',
      border: '1px solid #333333',
      padding: isMobile ? '12px' : '20px',
      paddingBottom: isMobile ? '12px' : 'calc(20px + 0.35cm)',
      width: '100%',
      position: 'relative'
    }}>
      <div style={{
        fontSize: isMobile ? '12px' : '14px',
        color: '#a0a0a0',
        fontWeight: '500',
        marginBottom: isMobile ? '8px' : '10px'
      }}>
        Credit Filter
      </div>
      
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : '0',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <div 
          ref={filterBoxRef}
          onClick={toggleDropdown}
          style={{
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center',
            padding: isMobile ? '12px' : '8px 12px',
            backgroundColor: '#252525',
            borderRadius: '6px',
            border: '1px solid #333333',
            width: isMobile ? '100%' : '85%'
          }}
        >
          <div style={{
            fontSize: isMobile ? '13px' : '14px',
            color: filters.startDate || filters.endDate || filters.minAmount || filters.maxAmount
              ? 'white' 
              : '#a0a0a0'
          }}>
            {getActiveFiltersText()}
          </div>
          <div style={{marginLeft: 'auto'}}>
            {isOpen ? (
              <ChevronUp size={isMobile ? 14 : 16} color="#a0a0a0" />
            ) : (
              <ChevronDown size={isMobile ? 14 : 16} color="#a0a0a0" />
            )}
          </div>
        </div>
        
        <button 
          onClick={handleResetFilters}
          style={{
            backgroundColor: '#333333',
            border: 'none',
            borderRadius: '50%',
            width: isMobile ? '40px' : '32px',
            height: isMobile ? '40px' : '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            marginLeft: isMobile ? '0' : '8px',
            alignSelf: isMobile ? 'center' : 'auto'
          }}
          title="Reset Filters"
        >
          <RefreshCw size={isMobile ? 18 : 16} color="white" />
        </button>
      </div>
      
      {isOpen && (
        <div style={{
          position: 'fixed',
          zIndex: 100,
          backgroundColor: '#252525',
          border: '1px solid #333333',
          borderRadius: '6px',
          padding: isMobile ? '12px' : '16px',
          width: isMobile ? '90vw' : '300px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
          top: `${dropdownPosition.top}px`,
          left: isMobile ? '5vw' : `${dropdownPosition.left}px`
        }}>
          {/* Date Validation Error */}
          {dateValidationError && (
            <div style={{
              backgroundColor: 'rgba(238, 85, 85, 0.1)',
              border: '1px solid rgba(238, 85, 85, 0.3)',
              borderRadius: '4px',
              padding: isMobile ? '8px' : '10px',
              marginBottom: isMobile ? '12px' : '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertTriangle size={14} color="#ff7777" />
              <span style={{
                fontSize: isMobile ? '12px' : '13px',
                color: '#ff9999'
              }}>
                {dateValidationError}
              </span>
            </div>
          )}
          
          {/* Date Range Section */}
          <div style={{marginBottom: isMobile ? '12px' : '14.4px'}}>
            <div 
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                padding: isMobile ? '8px 0' : '7.2px 0',
                borderBottom: '1px solid #333333'
              }}
              onClick={() => toggleSection('date')}
            >
              <div style={{fontWeight: '500', fontSize: isMobile ? '14px' : '12.6px', color: 'white'}}>
                Date Range
              </div>
              <div>
                {activeSection === 'date' ? (
                  <ChevronUp size={isMobile ? 16 : 14} color="#a0a0a0" />
                ) : (
                  <ChevronDown size={isMobile ? 16 : 14} color="#a0a0a0" />
                )}
              </div>
            </div>
            
            {activeSection === 'date' && (
              <div style={{
                marginTop: isMobile ? '12px' : '10.8px', 
                display: 'flex', 
                gap: isMobile ? '8px' : '10.8px',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <div style={{flex: 1}}>
                  <div style={{fontSize: isMobile ? '12px' : '10.8px', color: '#a0a0a0', marginBottom: isMobile ? '4px' : '3.6px'}}>From</div>
                  <input 
                    type="date" 
                    value={tempFilters.startDate || ''}
                    min={SERVICE_START_DATE}
                    max={getCurrentDate()}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    style={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333333',
                      borderRadius: '4px',
                      padding: isMobile ? '10px' : '7.2px',
                      color: 'white',
                      width: '100%',
                      fontSize: isMobile ? '14px' : '12.6px'
                    }}
                  />
                </div>
                <div style={{flex: 1}}>
                  <div style={{fontSize: isMobile ? '12px' : '10.8px', color: '#a0a0a0', marginBottom: isMobile ? '4px' : '3.6px'}}>To</div>
                  <input 
                    type="date" 
                    value={tempFilters.endDate || ''}
                    min={SERVICE_START_DATE}
                    max={getCurrentDate()}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    style={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333333',
                      borderRadius: '4px',
                      padding: isMobile ? '10px' : '7.2px',
                      color: 'white',
                      width: '100%',
                      fontSize: isMobile ? '14px' : '12.6px'
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Amount Range Section */}
          <div style={{marginBottom: isMobile ? '14px' : '16px'}}>
            <div 
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                padding: isMobile ? '8px 0' : '8px 0',
                borderBottom: '1px solid #333333'
              }}
              onClick={() => toggleSection('amount')}
            >
              <div style={{fontWeight: '500', fontSize: isMobile ? '14px' : '14px', color: 'white'}}>
                Amount Range
              </div>
              <div>
                {activeSection === 'amount' ? (
                  <ChevronUp size={isMobile ? 16 : 16} color="#a0a0a0" />
                ) : (
                  <ChevronDown size={isMobile ? 16 : 16} color="#a0a0a0" />
                )}
              </div>
            </div>
            
            {activeSection === 'amount' && (
              <div style={{marginTop: isMobile ? '12px' : '12px'}}>
                {/* Toggle buttons for preset/custom */}
                <div style={{
                  display: 'flex',
                  gap: isMobile ? '10px' : '8px',
                  marginBottom: isMobile ? '14px' : '12px',
                  flexDirection: isMobile ? 'column' : 'row'
                }}>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '10px' : '6px',
                      backgroundColor: !customAmountMode ? '#e0eacc' : '#1a1a1a',
                      color: !customAmountMode ? 'black' : 'white',
                      border: !customAmountMode ? '1px solid #e0eacc' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      logBackendCorrelation('deposits_filter_mode_changed', { mode: 'presets' });
                      setCustomAmountMode(false);
                    }}
                  >
                    Presets
                  </button>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '10px' : '6px',
                      backgroundColor: customAmountMode ? '#e0eacc' : '#1a1a1a',
                      color: customAmountMode ? 'black' : 'white',
                      border: customAmountMode ? '1px solid #e0eacc' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      logBackendCorrelation('deposits_filter_mode_changed', { mode: 'custom' });
                      setCustomAmountMode(true);
                    }}
                  >
                    Custom
                  </button>
                </div>
                
                {!customAmountMode ? (
                  // Preset amount ranges
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: isMobile ? '10px' : '8px'
                  }}>
                    <button 
                      style={{
                        padding: isMobile ? '12px' : '8px',
                        backgroundColor: (!tempFilters.minAmount && !tempFilters.maxAmount) ? '#1c2b22' : '#1a1a1a',
                        color: (!tempFilters.minAmount && !tempFilters.maxAmount) ? '#e0eacc' : 'white',
                        border: (!tempFilters.minAmount && !tempFilters.maxAmount) ? '1px solid #2a3c32' : '1px solid #333333',
                        borderRadius: '4px',
                        fontSize: isMobile ? '14px' : '13px',
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                      onClick={() => handlePresetSelect(null, null)}
                    >
                      All Amounts
                    </button>
                    
                    {getAmountPresets().map((preset, index) => (
                      <button 
                        key={index}
                        style={{
                          padding: isMobile ? '12px' : '8px',
                          backgroundColor: 
                            (tempFilters.minAmount === (preset.min ? preset.min.toString() : '') && 
                             tempFilters.maxAmount === (preset.max ? preset.max.toString() : '')) 
                              ? '#1c2b22' : '#1a1a1a',
                          color: 
                            (tempFilters.minAmount === (preset.min ? preset.min.toString() : '') && 
                             tempFilters.maxAmount === (preset.max ? preset.max.toString() : '')) 
                              ? '#e0eacc' : 'white',
                          border: 
                            (tempFilters.minAmount === (preset.min ? preset.min.toString() : '') && 
                             tempFilters.maxAmount === (preset.max ? preset.max.toString() : '')) 
                              ? '1px solid #2a3c32' : '1px solid #333333',
                          borderRadius: '4px',
                          fontSize: isMobile ? '14px' : '13px',
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                        onClick={() => handlePresetSelect(preset.min, preset.max)}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                ) : (
                  // Custom amount range
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: isMobile ? '14px' : '12px'
                  }}>
                    <div>
                      <div style={{fontSize: isMobile ? '13px' : '12px', color: '#a0a0a0', marginBottom: isMobile ? '6px' : '4px'}}>
                        Min Amount
                      </div>
                      <div style={{position: 'relative'}}>
                        <span style={{
                          position: 'absolute',
                          left: '8px',
                          top: isMobile ? '12px' : '8px',
                          color: '#a0a0a0'
                        }}>
                          {currencyUtils.getSymbol(currency)}
                        </span>
                        <input 
                          type="text" 
                          value={tempFilters.minAmount || ''}
                          onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                          style={{
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #333333',
                            borderRadius: '4px',
                            padding: isMobile ? '11px' : '7px',
                            paddingLeft: isMobile ? '24px' : '20px',
                            color: 'white',
                            width: '100%',
                            fontSize: isMobile ? '14px' : '13px'
                          }}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <div style={{fontSize: isMobile ? '13px' : '12px', color: '#a0a0a0', marginBottom: isMobile ? '6px' : '4px'}}>
                        Max Amount
                      </div>
                      <div style={{position: 'relative'}}>
                        <span style={{
                          position: 'absolute',
                          left: '8px',
                          top: isMobile ? '12px' : '8px',
                          color: '#a0a0a0'
                        }}>
                          {currencyUtils.getSymbol(currency)}
                        </span>
                        <input 
                          type="text" 
                          value={tempFilters.maxAmount || ''}
                          onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                          style={{
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #333333',
                            borderRadius: '4px',
                            padding: isMobile ? '11px' : '7px',
                            paddingLeft: isMobile ? '24px' : '20px',
                            color: 'white',
                            width: '100%',
                            fontSize: isMobile ? '14px' : '13px'
                          }}
                          placeholder="Any"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Apply Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: isMobile ? '14px' : '16px'
          }}>
            <button 
              style={{
                backgroundColor: dateValidationError ? '#666666' : '#e0eacc',
                color: dateValidationError ? '#999999' : 'black',
                border: 'none',
                borderRadius: '4px',
                padding: isMobile ? '12px 20px' : '8px 16px',
                fontWeight: '500',
                cursor: dateValidationError ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '14px' : '13px',
                width: isMobile ? '100%' : 'auto'
              }}
              onClick={handleApplyFilters}
              disabled={!!dateValidationError}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// MobileDepositCard component remains unchanged
const MobileDepositCard = ({ deposit, currency }) => {
  const { isMobile } = useResponsive();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
    
    logBackendCorrelation('mobile_deposit_card_toggled', {
      depositId: deposit.id,
      isExpanded: !isExpanded
    });
  };

  return (
    <div style={{
      backgroundColor: '#1a1a1a',
      borderRadius: '8px',
      border: '1px solid #333333',
      padding: isMobile ? '14px' : '16px',
      marginBottom: isMobile ? '10px' : '12px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: isMobile ? '6px' : '8px'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: isMobile ? '11px' : '12px',
            color: '#a0a0a0',
            marginBottom: '3px',
            fontFamily: 'monospace'
          }}>
            {deposit.id}
          </div>
          <div style={{
            fontSize: isMobile ? '13px' : '14px',
            color: 'white',
            fontWeight: '500',
            marginBottom: '3px'
          }}>
            Deposit
          </div>
          <div style={{
            fontSize: isMobile ? '11px' : '12px',
            color: '#a0a0a0'
          }}>
            {formatDate(deposit.timestamp, deposit.currency)}
          </div>
        </div>
        <div style={{
          textAlign: 'right',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '3px'
        }}>
          <div style={{ fontSize: isMobile ? '15px' : '16px', fontWeight: '600', color: '#e0eacc' }}>
            {currencyUtils.getSymbol(deposit.currency || currency)}{safeParseFloat(deposit.total_amount).toFixed(2)}
          </div>
          <StatusBadge status={deposit.status} />
        </div>
      </div>
      
      <div style={{
        fontSize: isMobile ? '12px' : '13px',
        color: '#e0e0e0',
        marginBottom: isMobile ? '6px' : '8px',
        lineHeight: '1.3'
      }}>
        Payment Method: {deposit.payment_method}
      </div>
      
      <button
        onClick={handleToggleExpand}
        style={{
          backgroundColor: 'transparent',
          border: '1px solid #333333',
          borderRadius: '4px',
          padding: isMobile ? '8px 12px' : '6px 12px',
          color: '#a0a0a0',
          fontSize: isMobile ? '11px' : '12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          width: isMobile ? '100%' : 'auto',
          justifyContent: isMobile ? 'center' : 'flex-start'
        }}
      >
        {isExpanded ? 'Less Details' : 'More Details'}
        {isExpanded ? <ChevronUp size={isMobile ? 14 : 12} /> : <ChevronDown size={isMobile ? 14 : 12} />}
      </button>
      
      {isExpanded && (
        <div style={{
          marginTop: isMobile ? '10px' : '12px',
          paddingTop: isMobile ? '10px' : '12px',
          borderTop: '1px solid #333333'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: isMobile ? '10px' : '8px',
            fontSize: isMobile ? '11px' : '12px'
          }}>
            <div>
              <span style={{ color: '#a0a0a0' }}>Base Amount:</span>
              <div style={{ marginTop: '2px', color: 'white' }}>
                {currencyUtils.getSymbol(deposit.currency || currency)}{safeParseFloat(deposit.amount).toFixed(2)}
              </div>
            </div>
            <div>
              <span style={{ color: '#a0a0a0' }}>Tax:</span>
              <div style={{ marginTop: '2px', color: 'white' }}>
                {deposit.gst_amount 
                  ? `${currencyUtils.getSymbol(deposit.currency || currency)}${safeParseFloat(deposit.gst_amount).toFixed(2)}`
                  : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ... rest of the DepositsTab component remains unchanged ...
const DepositsTab = ({ barrackId }) => {
  const { isMobile, isTablet } = useResponsive();
  const notification = useNotification();
  const { handleAuthError } = useAuth();
  const [allDeposits, setAllDeposits] = useState([]);
  const [displayDeposits, setDisplayDeposits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currency, setCurrency] = useState(currencyUtils.defaultCurrency);
  const [error, setError] = useState(null);
  const [lastShownError, setLastShownError] = useState('');
  const [summary, setSummary] = useState({
    totalCount: 0,
    totalTax: 0,
    avgAmount: 0
  });
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 20
  });
  
  // Performance: Initial load tracking
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  
  // Performance: Request cancellation
  
  // Performance: Retry logic
  const maxRetries = 3;
  const retryAttempts = useRef(0);
  
  const prevFiltersRef = useRef(filters);
  const isInitialLoad = useRef(true);

  // Validation helper functions
  const isValidDate = (dateString) => {
    if (!dateString) return true;
    try {
      const date = new Date(dateString);
      const minDate = new Date('2020-01-01');
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + 30);
      
      return !isNaN(date.getTime()) && date >= minDate && date <= maxDate;
    } catch {
      return false;
    }
  };

  const isValidAmount = (amountString) => {
    if (!amountString) return true;
    try {
      const amount = parseFloat(amountString);
      return !isNaN(amount) && amount >= 0 && amount <= 1000000;
    } catch {
      return false;
    }
  };

  const validateFilters = (currentFilters) => {
    const validationErrors = [];
    
    if (currentFilters.startDate && !isValidDate(currentFilters.startDate)) {
      validationErrors.push('Start date is invalid');
    }
    
    if (currentFilters.endDate && !isValidDate(currentFilters.endDate)) {
      validationErrors.push('End date is invalid');
    }
    
    if (currentFilters.minAmount && !isValidAmount(currentFilters.minAmount)) {
      validationErrors.push('Minimum amount must be between 0 and 1,000,000');
    }
    
    if (currentFilters.maxAmount && !isValidAmount(currentFilters.maxAmount)) {
      validationErrors.push('Maximum amount must be between 0 and 1,000,000');
    }
    
    if (currentFilters.minAmount && currentFilters.maxAmount) {
      const min = parseFloat(currentFilters.minAmount);
      const max = parseFloat(currentFilters.maxAmount);
      if (!isNaN(min) && !isNaN(max) && min > max) {
        validationErrors.push('Minimum amount cannot be greater than maximum amount');
      }
    }
    
    return validationErrors;
  };

  // Set Sentry context for DepositsTab
  useEffect(() => {
    Sentry.setContext("deposits-tab", {
      barrackId,
      isMobile,
      isTablet,
      currentFilters: filters,
      currentPage: pagination.currentPage,
      totalItems: pagination.totalItems,
      currency
    });
  }, [barrackId, isMobile, isTablet, filters, pagination, currency]);

  // Convert errors to toast notifications (don't clear error for persistent display)
  useEffect(() => {
    if (error && error !== lastShownError) {
      notification.error(error);
      setLastShownError(error);
      // Don't clear error - let persistent UI handle it
    }
  }, [error, notification, lastShownError]);

  // Component mount logging
  useEffect(() => {
    logBackendCorrelation('deposits_tab_mounted', {
      barrackId,
      isMobile,
      isTablet
    });
  }, []);



  // Performance: Debounced fetch function
  const debouncedFetchAllDeposits = useMemo(
    () => debounce(async (currentFilters = filters) => {
      await fetchAllDeposits(currentFilters);
    }, 300),
    [filters]
  );

  // Initial data loading
  useEffect(() => {
    fetchUserCurrency();
    fetchAllDeposits(filters);
    isInitialLoad.current = false;
  }, [barrackId]);
  
  // Handle filter changes
  useEffect(() => {
    if (!isInitialLoad.current && JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters)) {
      logBackendCorrelation('deposits_filters_changed', {
        previousFilters: prevFiltersRef.current,
        newFilters: filters
      });
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      fetchAllDeposits(filters);
      prevFiltersRef.current = {...filters};
    }
  }, [filters]);

// Fetch user currency preference
// Fetch user currency preference
const fetchUserCurrency = async () => {
  const startTime = performance.now();
  
  try {
    logBackendCorrelation('fetch_user_currency_started', { barrackId });
    
    const response = await api.getUserAuthProfile();
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Process security headers
    processSecurityHeaders(response);
    
    // Extract request ID for correlation
    const requestId = extractRequestId(response);
    
    // Handle direct response data (service returns response.data)
    const profileData = response || {};
    
    if (profileData.preferred_currency) {
      setCurrency(profileData.preferred_currency);
      
      logPerformance('fetch_user_currency', duration, {
        success: true,
        currency: profileData.preferred_currency,
        barrackId,
        request_id: requestId
      });
      
      logBackendCorrelation('user_currency_loaded', {
        currency: profileData.preferred_currency,
        duration: `${duration.toFixed(2)}ms`,
        request_id: requestId
      });
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      handleAuthError();
      return;
    }
    
    // Position 2: Network error (no status means network-level failure)
    if (!err.status) {
      const errorCode = err.code || 'NETWORK_ERROR';
      const errorMessage = err.error || ERROR_MESSAGES[errorCode] || 'Network error occurred';
      logError('Network error in fetchUserCurrency', { 
        code: errorCode, 
        message: errorMessage,
        online: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`,
        barrackId
      }, err);
      return;
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      logError('Currency fetch rate limited', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 4: Known error code
    if (err.code && ERROR_MESSAGES[err.code]) {
      logError('Currency fetch error with code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 5: Fallback
    logError('Error fetching user currency preference', {
      duration: `${duration.toFixed(2)}ms`,
      barrackId,
      errorCode: err.code,
      errorMessage: err.error
    }, err);
  }
};
// Fetch all deposits with current filters

// Fetch all deposits with current filters
const fetchAllDeposits = async (currentFilters = filters) => {
  const startTime = performance.now();
  
  // Check retry limit
  if (retryAttempts.current >= maxRetries) {
    setError('Maximum refresh attempts reached. Please wait before trying again.');
    logError('Maximum deposit fetch attempts exceeded', {
      attempts: retryAttempts.current,
      maxRetries
    });
    return;
  }
  
  retryAttempts.current++;
  setIsLoading(true);
  setError(null);
  
  try {
    // Validate filters before making API call
    const validationErrors = validateFilters(currentFilters);
    if (validationErrors.length > 0) {
      throw new Error(`Validation failed: ${validationErrors.join(', ')}`);
    }
    
    const params = {
      page: 1,
      limit: 100,
      barrack_id: barrackId,
      status: 'completed'
    };
    
    // Only add validated parameters
    if (currentFilters.startDate && isValidDate(currentFilters.startDate)) {
      params.start_date = currentFilters.startDate;
    }
    if (currentFilters.endDate && isValidDate(currentFilters.endDate)) {
      params.end_date = currentFilters.endDate;
    }
    if (currentFilters.minAmount && isValidAmount(currentFilters.minAmount)) {
      params.min_amount = currentFilters.minAmount;
    }
    if (currentFilters.maxAmount && isValidAmount(currentFilters.maxAmount)) {
      params.max_amount = currentFilters.maxAmount;
    }
    
    logBackendCorrelation('fetch_deposits_started', { params, barrackId });
    
    const response = await api.getDeposits(params);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Process security headers
    processSecurityHeaders(response);
    
    // Extract request ID for correlation
    const requestId = extractRequestId(response);
    
    // Handle direct response data from service layer
    const responseData = response || {};
    
    if (responseData.results || Array.isArray(responseData)) {
      const deposits = responseData.results || responseData || [];
      
      const sortedDeposits = [...deposits].sort((a, b) => {
        const dateA = new Date(a.timestamp);
        const dateB = new Date(b.timestamp);
        return dateB - dateA;
      });
      
      setAllDeposits(sortedDeposits);
      applyPagination(sortedDeposits, pagination.currentPage);
      await fetchDepositSummary(currentFilters, requestId);
      
      if (!initialDataLoaded) setInitialDataLoaded(true);
      
      retryAttempts.current = 0; // Reset on success
      
      logPerformance('fetch_deposits', duration, {
        success: true,
        depositCount: sortedDeposits.length,
        barrackId,
        request_id: requestId
      });
      
      logBackendCorrelation('deposits_loaded', {
        depositCount: sortedDeposits.length,
        duration: `${duration.toFixed(2)}ms`,
        request_id: requestId
      });
    } else {
      const errorMsg = responseData.error || responseData.message || 'Invalid response format: Expected array of deposits';
      throw new Error(errorMsg);
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      handleAuthError();
      setIsLoading(false);
      return;
    }
    
    // Position 2: Network error (no status means network-level failure)
    if (!err.status && err.code) {
      const errorMessage = err.error || ERROR_MESSAGES[err.code] || 'Network error occurred';
      setError(errorMessage);
      logError('Network error in fetchAllDeposits', { 
        code: err.code, 
        message: errorMessage,
        online: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`,
        barrackId
      }, err);
      setIsLoading(false);
      return;
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      setError(err.error || 'Too many requests. Please wait before trying again.');
      logError('Deposits fetch rate limited', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      setIsLoading(false);
      return;
    }
    
    // Position 4: Known error code
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      logError('Deposits fetch error with code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      setIsLoading(false);
      return;
    }
    
    // Position 5: Fallback
    const errorMessage = getSafeErrorMessage(err);
    setError(errorMessage);
    
    logError('Error fetching deposits', {
      duration: `${duration.toFixed(2)}ms`,
      currentFilters,
      barrackId,
      errorCode: err.code,
      errorMessage: err.error
    }, err);
    
    logPerformance('fetch_deposits', duration, {
      success: false,
      errorCode: err.code
    });
  } finally {
    setIsLoading(false);
  }
};

// Fetch summary data including average amount and total tax
const fetchDepositSummary = async (currentFilters = filters, parentRequestId = null) => {
  const startTime = performance.now();
  
  // Helper for client-side fallback calculation
  const calculateClientSideSummary = () => {
    if (allDeposits.length > 0) {
      const totalAmount = allDeposits.reduce((sum, deposit) => 
        sum + safeParseFloat(deposit.amount), 0);
      const totalTax = allDeposits.reduce((sum, deposit) => 
        sum + safeParseFloat(deposit.gst_amount || 0), 0);
      
      setSummary({
        totalCount: allDeposits.length,
        totalTax: totalTax,
        avgAmount: totalAmount / allDeposits.length
      });
      
      return {
        totalCount: allDeposits.length,
        totalTax,
        avgAmount: totalAmount / allDeposits.length
      };
    }
    return null;
  };
  
  try {
    // Validate filters before making API call
    const validationErrors = validateFilters(currentFilters);
    if (validationErrors.length > 0) {
      logError('Summary fetch skipped due to validation errors', {
        validationErrors,
        currentFilters
      });
      return;
    }
    
    const params = {
      barrack_id: barrackId
    };
    
    // Only add validated parameters
    if (currentFilters.startDate && isValidDate(currentFilters.startDate)) {
      params.start_date = currentFilters.startDate;
    }
    if (currentFilters.endDate && isValidDate(currentFilters.endDate)) {
      params.end_date = currentFilters.endDate;
    }
    if (currentFilters.minAmount && isValidAmount(currentFilters.minAmount)) {
      params.min_amount = currentFilters.minAmount;
    }
    if (currentFilters.maxAmount && isValidAmount(currentFilters.maxAmount)) {
      params.max_amount = currentFilters.maxAmount;
    }
    
    logBackendCorrelation('fetch_deposit_summary_started', { 
      params, 
      barrackId,
      parent_request_id: parentRequestId 
    });
    
    const response = await api.getDepositsSummary(params);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Process security headers
    processSecurityHeaders(response);
    
    // Extract request ID for correlation
    const requestId = extractRequestId(response);
    
    // Handle direct response data from service layer
    const summaryData = response || {};
    
    if (summaryData.total_count !== undefined || summaryData.total_tax !== undefined || summaryData.avg_amount !== undefined) {
      setSummary({
        totalCount: summaryData.total_count || 0,
        totalTax: summaryData.total_tax || 0,
        avgAmount: summaryData.avg_amount || 0
      });
      
      logPerformance('fetch_deposit_summary', duration, {
        success: true,
        barrackId,
        request_id: requestId,
        parent_request_id: parentRequestId
      });
    } else {
      // Fallback calculation from client data
      const clientSummary = calculateClientSideSummary();
      if (clientSummary) {
        logBackendCorrelation('deposit_summary_calculated_client', {
          ...clientSummary,
          request_id: requestId,
          parent_request_id: parentRequestId
        });
      }
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      handleAuthError();
      return;
    }
    
    // Position 2: Network error (no status means network-level failure)
    if (!err.status && err.code) {
      logError('Network error in fetchDepositSummary', { 
        code: err.code, 
        message: err.error,
        online: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`,
        barrackId,
        parent_request_id: parentRequestId
      }, err);
      
      // Fallback to client-side calculation
      const clientSummary = calculateClientSideSummary();
      if (clientSummary) {
        logBackendCorrelation('deposit_summary_fallback_client_network_error', clientSummary);
      }
      return;
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      logError('Summary fetch rate limited', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`,
        parent_request_id: parentRequestId
      }, err);
      
      // Fallback to client-side calculation
      calculateClientSideSummary();
      return;
    }
    
    // Position 4: Known error code
    if (err.code && ERROR_MESSAGES[err.code]) {
      logError('Summary fetch error with code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`,
        parent_request_id: parentRequestId
      }, err);
      
      // Fallback to client-side calculation
      calculateClientSideSummary();
      return;
    }
    
    // Position 5: Fallback
    logError('Error fetching deposit summary', {
      duration: `${duration.toFixed(2)}ms`,
      barrackId,
      errorCode: err.code,
      errorMessage: err.error,
      parent_request_id: parentRequestId
    }, err);
    
    // Fallback to client-side calculation
    const clientSummary = calculateClientSideSummary();
    if (clientSummary) {
      logBackendCorrelation('deposit_summary_fallback_client', {
        ...clientSummary,
        parent_request_id: parentRequestId
      });
    }
  }
};
  // Apply client-side pagination to the sorted data
  const applyPagination = (data, page) => {
    const pageSize = pagination.limit;
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    
    const pageItems = data.slice(start, end);
    const totalPages = Math.ceil(data.length / pageSize);
    
    logBackendCorrelation('deposits_pagination_applied', {
      totalItems: data.length,
      currentPage: page,
      totalPages,
      pageSize,
      displayedItems: pageItems.length
    });
    
    setDisplayDeposits(pageItems);
    setPagination(prev => ({
      ...prev,
      currentPage: page,
      totalPages: totalPages || 1,
      totalItems: data.length
    }));
  };

  // Handle filter changes with validation
  const applyFilters = (overrideValues = {}) => {
    const updatedFilters = { ...filters, ...overrideValues };
    
    // Validate new filters
    const validationErrors = validateFilters(updatedFilters);
    if (validationErrors.length > 0) {
      setError(`Filter validation failed: ${validationErrors.join(', ')}`);
      logError('Filter validation failed', {
        updatedFilters,
        validationErrors
      });
      return;
    }
    
    // Clear any existing errors
    setError(null);
    
    logBackendCorrelation('deposits_filters_applied', {
      previousFilters: filters,
      newFilters: updatedFilters,
      overrideValues
    });
    
    setFilters(updatedFilters);
  };

  // Reset all filters
  const resetFilters = () => {
    logBackendCorrelation('deposits_filters_reset_all', {
      previousFilters: filters
    });
    
    setError(null);
    setFilters({
      startDate: '',
      endDate: '',
      minAmount: '',
      maxAmount: ''
    });
  };

  // Handle pagination changes
  const handlePageChange = (newPage) => {
    if (newPage !== pagination.currentPage && newPage > 0 && newPage <= pagination.totalPages) {
      logBackendCorrelation('deposits_page_changed', {
        previousPage: pagination.currentPage,
        newPage,
        totalPages: pagination.totalPages,
        totalItems: pagination.totalItems
      });
      
      applyPagination(allDeposits, newPage);
    }
  };

  // Component unmount cleanup
  useEffect(() => {
    return () => {
      logBackendCorrelation('deposits_tab_unmounted', {
        barrackId,
        finalState: {
          totalDeposits: allDeposits.length,
          currentPage: pagination.currentPage,
          appliedFilters: filters
        }
      });
    };
  }, []);

  // Render desktop/tablet layout
  if (!isMobile) {
    return (
      <div>
        <div style={{
          display: 'flex',
          gap: isTablet ? '12px' : '20px',
          marginBottom: '28px',
          flexWrap: isTablet ? 'wrap' : 'nowrap'
        }}>
          {/* Total Credits Card */}
          <div style={{
            backgroundColor: '#1a1a1a',
            borderRadius: '10px',
            border: '1px solid #333333',
            padding: isTablet ? '16px' : '20px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
            position: 'relative',
            width: isTablet ? 'calc(50% - 6px)' : 'calc(20% - 20px)',
            height: isTablet ? 'auto' : 'calc(100% - 3cm)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#a0a0a0',
                fontWeight: '500'
              }}>
                Total Credits
              </div>
            </div>
            <div style={{
              fontSize: isTablet ? '24px' : '28px',
              fontWeight: '600',
              color: 'white'
            }}>
              {summary.totalCount}
            </div>
          </div>
          
          {/* Average Credit Card */}
          <div style={{
            backgroundColor: '#1a1a1a',
            borderRadius: '10px',
            border: '1px solid #333333',
            padding: isTablet ? '16px' : '20px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
            position: 'relative',
            width: isTablet ? 'calc(50% - 6px)' : 'calc(20% - 20px)',
            marginLeft: isTablet ? '0' : '.2cm',
            height: isTablet ? 'auto' : 'calc(100% - 3cm)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#a0a0a0',
                fontWeight: '500'
              }}>
                Average Credit
              </div>
            </div>
            <div style={{
              fontSize: isTablet ? '24px' : '28px',
              fontWeight: '600',
              color: '#e0eacc'
            }}>
              {currencyUtils.getSymbol(currency)}{safeParseFloat(summary.avgAmount).toFixed(2)}
            </div>
          </div>
          
          {/* Total Tax Card */}
          <div style={{
            backgroundColor: '#1a1a1a',
            borderRadius: '10px',
            border: '1px solid #333333',
            padding: isTablet ? '16px' : '20px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
            position: 'relative',
            width: isTablet ? 'calc(50% - 6px)' : 'calc(20% - 20px)',
            marginLeft: isTablet ? '0' : '0cm',
            height: isTablet ? 'auto' : 'calc(100% - 3cm)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#a0a0a0',
                fontWeight: '500'
              }}>
                Total Tax
              </div>
            </div>
            <div style={{
              fontSize: isTablet ? '24px' : '28px',
              fontWeight: '600',
              color: '#ff9494'
            }}>
              {currencyUtils.getSymbol(currency)}{safeParseFloat(summary.totalTax).toFixed(2)}
            </div>
          </div>

          {/* Filter container */}
          <div style={{
            flex: 1,
            height: isTablet ? 'auto' : 'calc(70%)',
            marginRight: isTablet ? '0' : '-5.5cm',
            width: isTablet ? 'calc(50% - 6px)' : 'auto'
          }}>
            <div style={{ width: isTablet ? '100%' : '400px' }}>
              <DepositsSmartFilterContainer 
                filters={filters}
                setFilters={setFilters}
                applyFilters={applyFilters}
                resetFilters={resetFilters}
                currency={currency}
                isMobile={isMobile}
                isTablet={isTablet}
              />
            </div>
          </div>
        </div>

        {/* Persistent Error Display with Retry */}
        {error && (
          <div style={{
            marginBottom: '24px',
            backgroundColor: 'rgba(238, 85, 85, 0.1)',
            border: '1px solid rgba(238, 85, 85, 0.3)',
            borderRadius: '8px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
              <AlertTriangle size={20} color="#ff7777" />
              <p style={{ color: '#ff9999', fontSize: '14px', margin: 0 }}>{error}</p>
            </div>
            <button
              onClick={() => {
                setError(null);
                retryAttempts.current = 0;
                debouncedFetchAllDeposits();
              }}
              style={{
                backgroundColor: 'rgba(238, 85, 85, 0.2)',
                border: '1px solid rgba(238, 85, 85, 0.4)',
                borderRadius: '4px',
                padding: '8px 16px',
                color: '#ff9999',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(238, 85, 85, 0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(238, 85, 85, 0.2)';
              }}
            >
              <RefreshCw size={14} />
              Retry
            </button>
          </div>
        )}

        {isLoading ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 0',
            backgroundColor: '#1a1a1a',
            borderRadius: '10px',
            border: '1px solid #333333',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
            color: '#a0a0a0',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}>
            <Loader2 className="animate-spin" size={24} />
            Loading credits...
          </div>

        ) : (
          <>
            <div style={{
              backgroundColor: '#1a1a1a',
              borderRadius: '10px',
              border: '1px solid #333333',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
              overflowX: 'auto'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: '0'
              }}>
                <thead>
                  <tr>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Credit ID</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Date & Time</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Base Amount</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Tax</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Total</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Payment Method</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {displayDeposits.length > 0 ? (
                    displayDeposits.map((deposit) => (
                      <tr 
                        key={deposit.id}
                        style={{
                          transition: 'background-color 0.15s ease'
                        }}
                      >
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
                          fontFamily: 'monospace',
                          opacity: '0.8',
                          textAlign: deposit.id ? 'left' : 'center'
                        }}>{deposit.id || '-'}</td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>{formatDate(deposit.timestamp, deposit.currency)}</td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>
                          {currencyUtils.getSymbol(deposit.currency || currency)}{safeParseFloat(deposit.amount).toFixed(2)}
                        </td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>
                          {deposit.gst_amount 
                            ? `${currencyUtils.getSymbol(deposit.currency || currency)}${safeParseFloat(deposit.gst_amount).toFixed(2)}`
                            : 'N/A'}
                        </td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>
                          {currencyUtils.getSymbol(deposit.currency || currency)}{safeParseFloat(deposit.total_amount).toFixed(2)}
                        </td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>{deposit.payment_method}</td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>
                          <StatusBadge status={deposit.status} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td 
                        colSpan="7" 
                        style={{
                          textAlign: 'center',
                          color: '#777777',
                          padding: '40px',
                          fontStyle: 'italic'
                        }}
                      >No credits found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {displayDeposits.length > 0 && (
              <Pagination 
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                isMobile={isMobile}
              />
            )}
          </>
        )}
      </div>
    );
  }

  // Mobile layout - MOBILE OPTIMIZED
  return (
    <div style={{ padding: isMobile ? '12px' : '16px' }}>
      {/* Mobile Summary Cards - 2x2 Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: isMobile ? '8px' : '12px',
        marginBottom: isMobile ? '16px' : '20px'
      }}>
        {/* Total Credits */}
        <div style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #333333',
          padding: isMobile ? '12px' : '16px'
        }}>
          <div style={{
            fontSize: isMobile ? '11px' : '12px',
            color: '#a0a0a0',
            fontWeight: '500',
            marginBottom: isMobile ? '6px' : '8px'
          }}>
            Total Credits
          </div>
          <div style={{
            fontSize: isMobile ? '18px' : '20px',
            fontWeight: '600',
            color: 'white'
          }}>
            {summary.totalCount}
          </div>
        </div>
        
        {/* Average Credit */}
        <div style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #333333',
          padding: isMobile ? '12px' : '16px'
        }}>
          <div style={{
            fontSize: isMobile ? '11px' : '12px',
            color: '#a0a0a0',
            fontWeight: '500',
            marginBottom: isMobile ? '6px' : '8px'
          }}>
            Average Credit
          </div>
          <div style={{
            fontSize: isMobile ? '18px' : '20px',
            fontWeight: '600',
            color: '#e0eacc'
          }}>
            {currencyUtils.getSymbol(currency)}{safeParseFloat(summary.avgAmount).toFixed(2)}
          </div>
        </div>
        
        {/* Total Tax */}
        <div style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #333333',
          padding: isMobile ? '12px' : '16px'
        }}>
          <div style={{
            fontSize: isMobile ? '11px' : '12px',
            color: '#a0a0a0',
            fontWeight: '500',
            marginBottom: isMobile ? '6px' : '8px'
          }}>
            Total Tax
          </div>
          <div style={{
            fontSize: isMobile ? '18px' : '20px',
            fontWeight: '600',
            color: '#ff9494'
          }}>
            {currencyUtils.getSymbol(currency)}{safeParseFloat(summary.totalTax).toFixed(2)}
          </div>
        </div>

        {/* Filter container */}
        <div style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #333333',
          padding: isMobile ? '8px' : '16px'
        }}>
          <DepositsSmartFilterContainer 
            filters={filters}
            setFilters={setFilters}
            applyFilters={applyFilters}
            resetFilters={resetFilters}
            currency={currency}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        </div>
      </div>

      {/* Mobile Persistent Error Display */}
      {error && (
        <div style={{
          marginBottom: '16px',
          backgroundColor: 'rgba(238, 85, 85, 0.1)',
          border: '1px solid rgba(238, 85, 85, 0.3)',
          borderRadius: '8px',
          padding: '12px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: '10px',
            marginBottom: '12px'
          }}>
            <AlertTriangle size={18} color="#ff7777" style={{ flexShrink: 0, marginTop: '2px' }} />
            <p style={{ 
              color: '#ff9999', 
              fontSize: '13px', 
              margin: 0,
              flex: 1,
              lineHeight: '1.4'
            }}>
              {error}
            </p>
          </div>
          <button
            onClick={() => {
              setError(null);
              retryAttempts.current = 0;
              debouncedFetchAllDeposits();
            }}
            style={{
              backgroundColor: 'rgba(238, 85, 85, 0.2)',
              border: '1px solid rgba(238, 85, 85, 0.4)',
              borderRadius: '4px',
              padding: '10px 16px',
              color: '#ff9999',
              fontSize: '13px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              cursor: 'pointer'
            }}
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      )}

      {isLoading ? (
        <div style={{
          textAlign: 'center',
          padding: isMobile ? '30px 16px' : '40px 20px',
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #333333',
          color: '#a0a0a0',
          fontSize: isMobile ? '13px' : '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? '6px' : '8px'
        }}>
          <Loader2 className="animate-spin" size={isMobile ? 18 : 20} />
          Loading credits...
        </div>

      ) : (
        <>
          {/* Mobile Deposit Cards */}
          <div>
            {displayDeposits.length > 0 ? (
              displayDeposits.map((deposit) => (
                <MobileDepositCard
                  key={deposit.id}
                  deposit={deposit}
                  currency={currency}
                />
              ))
            ) : (
              <div style={{
                textAlign: 'center',
                color: '#777777',
                padding: isMobile ? '30px 16px' : '40px 20px',
                fontStyle: 'italic',
                backgroundColor: '#1a1a1a',
                borderRadius: '8px',
                border: '1px solid #333333',
                fontSize: isMobile ? '13px' : '14px'
              }}>
                No credits found
              </div>
            )}
          </div>
          
          {displayDeposits.length > 0 && (
            <Pagination 
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              isMobile={isMobile}
            />
          )}
        </>
      )}
    </div>
  );
};

export default DepositsTab;